import React, { useRef } from 'react';
import Address from '../components/Address';
import BusinessHours from '../components/BusinessHours';
import EmployeeTable from '../components/EmployeeTable';
import { fetchAllEmployeesForBranch, fetchBranch } from '../api';
import {
  FetchBranchResult,
  FetchBranchInput,
  FetchEmployeeResult,
  FetchTeamResult
} from '../types';
import { usePromiseFunction, useScrollIntoViewOnMount } from '../hooks';

import styled from 'styled-components';
import css from '@styled-system/css';
import PageHeader from '../components/PageHeader';
import { NoMatch } from '../components/NoMatch';
import { H1, H2, H3, H4, H5, H6, Label, Spinner } from '@wco/toolkit';
import { BankOrCreditUnionSchema } from '../components/MicroData';
import bankSEOConfig from '../configs/index';
import SEO from '../components/SEO';
import { Route } from 'react-router-dom';
import appConfig from '../config';

const Rule = styled.div`
  position: relative;
  text-align: center;
  margin: 0 0 ${props => props.theme.space.xxxlarge};

  &::after {
    content: '';
    border-bottom: 1px solid rgba(217, 217, 217, 0.8);
    width: 100%;
    height: 1px;
    position: absolute;
    left: 0;
    top: 50%;
    z-index: 1;
  }
`;

const RuleAction = styled.span`
  display: inline-block;
  position: relative;
  height: ${props => props.theme.lineHeights.large};
  line-height: ${props => props.theme.lineHeights.large};
  padding: 0 ${props => props.theme.space.large};
  background-color: ${props => props.theme.ruleColor};
  color: #fff;
  z-index: 2;
  border-radius: ${props => (props.theme.name === 'Skjernbank' ? '4px' : '0')};

  &::after {
    content: '';
    border-bottom: 1px solid rgba(217, 217, 217, 0.8);
    width: 100%;
    height: 1px;
    left: 0;
    top: 50%;
    z-index: 1;
  }
`;

const Subheader = styled.h4`
  line-height: ${props => props.theme.lineHeights.small};
  color: ${props => props.theme.colors.palette.darkGrey};
  font-weight: normal;
  margin-top: 0;
  margin-bottom: ${props => props.theme.space.medium};
  font-family: Roboto, sans-serif;
  font-style: normal;
  font-size: ${props => props.theme.fontSizes.text.large};
`;

const FlexBox = styled.div<any>`
  ${css({
    display: ['block', 'flex']
  })}
  
  max-width: ${(props: any) => (props.maxWidth ? props.maxWidth : '100%')};
  margin-left: auto;
  margin-right: auto;
  justify-content: ${(props: any) =>
    props.description ? 'space-between' : 'start'};
`;

const FlexColLeft = styled.div`
  ${css({
    marginTop: ['0', 'medium'],
    marginBottom: ['small', 'medium'],
    marginRight: ['0', 'medium'],
    marginLeft: ['0', 'medium'],
    width: ['100%', '68.33%', '68.33%']
  })}

  &:empty {
    display: none;
  }
`;

const FlexColRight = styled.div`
  ${css({
    marginTop: ['0', 'medium'],
    marginBottom: ['small', 'medium'],
    marginRight: ['0', 'medium'],
    marginLeft: ['0', 'medium'],
    width: ['100%', 'auto']
  })}

  &:empty {
    display: none;
  }
`;

const Paragraph = styled.p`
  font-size: ${props => props.theme.fontSizes.medium};
  font-weight: normal;
  line-height: ${props => props.theme.lineHeights.xsmall};
  margin-bottom: ${props => props.theme.space.xlarge};
  text-rendering: optimizeLegibility;
`;

const BranchInner = styled.div`
  overflow-wrap: anywhere;
  padding: ${props => [props.theme.space.medium, props.theme.space.xlarge]};
`;

const Topbanner = styled.div<any>`
  ${css({
    height: ['auto', 'auto', '21.875rem']
  })}
  background-image: ${(props: any) => `url(${props.background})`};  
  padding: 0 3.125rem;
  position: relative;
  
  background-size: cover;
  background-position: 50% 0%;
  text-align: center;
`;

export function Branch(props: any) {
  const { nextRouter } = props;

  const input: FetchBranchInput = {
    branchName: props.match.params.branchname
  };

  const employeesForBranch = usePromiseFunction(
    fetchAllEmployeesForBranch,
    input.branchName
  );

  const branchNameForSEO = input.branchName.replace(/(^\w|\s\w)/g, m =>
    m.toUpperCase()
  );

  const branches = usePromiseFunction(fetchBranch, input.branchName);

  if (branches.state === 'pending' || employeesForBranch.state === 'pending') {
    return <Spinner />;
  } else if (
    branches.state === 'error' ||
    employeesForBranch.state === 'error'
  ) {
    if (appConfig.notFoundPage) {
      return (
        <>
          <Spinner />
          <Route
            path="*"
            component={() => {
              window.location.href = `${appConfig.notFoundPage}`;
              return null;
            }}
          />
        </>
      );
    } else {
      return (
        <NoMatch
          header={'Filialen findes ikke'}
          message={'Den filial du har søgt efter findes ikke'}
        />
      );
    }
  } else {
    return (
      <>
        <SEO
          title={`${bankSEOConfig.fullBankName} ${branchNameForSEO} - ${bankSEOConfig.branchPage.title}`}
          description={`Velkommen i ${bankSEOConfig.fullBankName} ${branchNameForSEO}. ${bankSEOConfig.branchPage.description}`}
        />

        <BranchContainer
          branchHeadlineSize={props.branchHeadlineSize}
          branch={branches.data}
          employees={employeesForBranch.data.employees}
          teams={employeesForBranch.data.teams}
          url={props.match.url}
          headerText={props.headerText}
          nextRouter={nextRouter}
        />
      </>
    );
  }
}

type BranchContainerProps = {
  branch: FetchBranchResult;
  employees: FetchEmployeeResult[] | undefined;
  teams: FetchTeamResult[] | undefined | null;
  url: string;
  headerText: string;
  branchHeadlineSize: string;
  nextRouter?: any;
};
const Wrapper = styled.div<any>``;
function BranchContainer(props: BranchContainerProps) {
  const { nextRouter } = props;
  const renderBranchName = (branch: FetchBranchResult) => {
    if (props.branchHeadlineSize === 'h1') {
      return <H1>{branch.branchName}</H1>;
    } else if (props.branchHeadlineSize === 'h2') {
      return <H2>{branch.branchName}</H2>;
    } else if (props.branchHeadlineSize === 'h3') {
      return <H3>{branch.branchName}</H3>;
    } else if (props.branchHeadlineSize === 'h4') {
      return <H4>{branch.branchName}</H4>;
    } else if (props.branchHeadlineSize === 'h5') {
      return <H5>{branch.branchName}</H5>;
    } else {
      return <H6>{branch.branchName}</H6>;
    }
  };

  const resultsRef = useRef();
  useScrollIntoViewOnMount(resultsRef);

  const { branch, employees, teams, url, headerText } = props;
  function createDescriptionMarkup() {
    return {
      __html: branch.description.replace(/(?:\r\n|\r|\n)/g, '<br>')
    };
  }
  const isBranchDescription = branch.description
    ? branch.description.trim() === ''
      ? false
      : true
    : false;
  return (
    <>
      <BranchInner data-testid="branch-container">
        <Wrapper ref={resultsRef}>
          {branch.url && (
            <Topbanner
              background={branch.url}
              alt={`Billede af filialen ${branch.branchName}`}
            />
          )}
          <div>
            <Label>Afdeling</Label>
            {renderBranchName(branch)}
            <Subheader>{branch.headerDescription}</Subheader>
          </div>
          <FlexBox
            maxWidth={
              isBranchDescription
                ? branch.description.trim()
                  ? '100%'
                  : 'auto'
                : 'auto'
            }
            description={
              isBranchDescription ? branch.description.trim().length !== 0 : ''
            }
          >
            <FlexColLeft>
              {isBranchDescription && (
                <Paragraph
                  dangerouslySetInnerHTML={createDescriptionMarkup()}
                />
              )}
            </FlexColLeft>
            {isBranchDescription ? (
              <FlexColRight>
                <H5>Kontaktoplysninger</H5>
                <Address {...branch} />
                <br />
                <H5>Åbningstider</H5>
                <BusinessHours opening={branch.openingHours}></BusinessHours>
              </FlexColRight>
            ) : (
              <>
                <FlexColRight>
                  <H5>Kontaktoplysninger</H5>
                  <Address {...branch} />
                </FlexColRight>
                <FlexColRight>
                  <H5>Åbningstider</H5>
                  <BusinessHours opening={branch.openingHours}></BusinessHours>
                </FlexColRight>
              </>
            )}
          </FlexBox>
          {employees && (
            <>
              <Rule>
                <RuleAction>Medarbejdere</RuleAction>
              </Rule>
              <PageHeader headerText={headerText} />
              <EmployeeTable
                employees={employees}
                url={url}
                teams={teams}
                nextRouter={nextRouter}
              ></EmployeeTable>
            </>
          )}
        </Wrapper>
      </BranchInner>
      <BankOrCreditUnionSchema {...branch} />
    </>
  );
}
